import { useMemo } from 'react';
import { useParams } from 'react-router';
import {
  Badge,
  ErrorState,
  InlineAlert,
  PaginatedTable,
  Panel,
  Progress,
  cn,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercentage,
  type BadgeTone,
} from 'escrowbuild-ui';
import type {
  AggregationSpec,
  DataSource,
  FilterSpec,
  RawCellValue,
  RemoteOption,
  Row,
  SortSpec,
  TableColumn,
  TableQuery,
  TableResult,
  TableSelectSource,
} from 'escrowbuild-ui';

import {
  useExpenses,
  useFixtureVocabulary,
  useScheduleOf,
  useT,
  type AppTranslate,
  type AppTranslationKey,
  type FixtureVocabulary,
} from '@/i18n';
import {
  findInvestor,
  findProject,
  participationsOf,
  projectTotals,
  type Expense,
  type Participation,
  type SchedulePhase,
} from '@/data/domain';

/* ---------------------------------------------------------------------------
   The table contract is server-side: filtering, sorting, aggregation and
   pagination clamping all happen behind this function, never by slicing an
   array inside the table. Mirrors the design system's own mock source so a
   real API can replace it unchanged.
   ------------------------------------------------------------------------ */

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('Aborted'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new Error('Aborted'));
      },
      { once: true },
    );
  });
}

function asNumber(value: RawCellValue): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function matchesFilter(row: Row, filter: FilterSpec): boolean {
  const cell = row[filter.field];
  const { op, value } = filter;

  switch (op) {
    case 'isNull':
      return cell === null || cell === undefined || cell === '';
    case 'isNotNull':
      return cell !== null && cell !== undefined && cell !== '';
    case 'eq':
      if (typeof cell === 'boolean' || typeof value === 'boolean') return cell === value;
      if (typeof cell === 'number') return asNumber(value as RawCellValue) === cell;
      return String(cell) === String(value);
    case 'ne':
      return !matchesFilter(row, { ...filter, op: 'eq' });
    case 'contains':
      return String(cell ?? '').toLowerCase().includes(String(value ?? '').toLowerCase());
    case 'startsWith':
      return String(cell ?? '').toLowerCase().startsWith(String(value ?? '').toLowerCase());
    case 'endsWith':
      return String(cell ?? '').toLowerCase().endsWith(String(value ?? '').toLowerCase());
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const a = asNumber(cell);
      const b = asNumber(value as RawCellValue);
      if (a === null || b === null) {
        const sa = String(cell ?? '');
        const sb = String(value ?? '');
        if (op === 'gt') return sa > sb;
        if (op === 'gte') return sa >= sb;
        if (op === 'lt') return sa < sb;
        return sa <= sb;
      }
      if (op === 'gt') return a > b;
      if (op === 'gte') return a >= b;
      if (op === 'lt') return a < b;
      return a <= b;
    }
    case 'between': {
      if (!Array.isArray(value) || value.length !== 2) return true;
      const [lo, hi] = value as [unknown, unknown];
      if (lo === null || lo === undefined || lo === '') {
        const upper = asNumber(hi as RawCellValue);
        return upper === null ? true : (asNumber(cell) ?? -Infinity) <= upper;
      }
      if (hi === null || hi === undefined || hi === '') {
        const lower = asNumber(lo as RawCellValue);
        return lower === null ? true : (asNumber(cell) ?? Infinity) >= lower;
      }
      const cellNum = asNumber(cell);
      const loNum = asNumber(lo as RawCellValue);
      const hiNum = asNumber(hi as RawCellValue);
      if (cellNum !== null && loNum !== null && hiNum !== null) {
        return cellNum >= loNum && cellNum <= hiNum;
      }
      const text = String(cell ?? '');
      return text >= String(lo) && text <= String(hi);
    }
    case 'in':
      if (!Array.isArray(value) || value.length === 0) return true;
      return value.some((member) => String(member) === String(cell));
    default:
      return true;
  }
}

function compareRows(a: Row, b: Row, sort: SortSpec): number {
  const av = a[sort.field] ?? null;
  const bv = b[sort.field] ?? null;
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  let result: number;
  if (typeof av === 'number' && typeof bv === 'number') result = av - bv;
  else if (typeof av === 'boolean' && typeof bv === 'boolean') result = Number(av) - Number(bv);
  else result = String(av).localeCompare(String(bv), 'en', { numeric: true, sensitivity: 'base' });

  return sort.dir === 'asc' ? result : -result;
}

function aggregate(rows: Row[], spec: AggregationSpec): number | null {
  const values = rows
    .map((row) => row[spec.field])
    .filter((value): value is string | number => typeof value === 'number' || typeof value === 'string');
  if (values.length === 0) return null;
  if (spec.op === 'count') return rows.length;
  if (spec.op === 'countDistinct') return new Set(values.map((value) => String(value))).size;

  const numbers = values
    .map((value) => asNumber(value as RawCellValue))
    .filter((value): value is number => value !== null);
  if (numbers.length === 0) return null;

  const round = (n: number) =>
    spec.precision === undefined ? n : Math.round(n * 10 ** spec.precision) / 10 ** spec.precision;

  switch (spec.op) {
    case 'sum':
      return round(numbers.reduce((total, n) => total + n, 0));
    case 'avg':
      return round(numbers.reduce((total, n) => total + n, 0) / numbers.length);
    case 'min':
      return Math.min(...numbers);
    case 'max':
      return Math.max(...numbers);
    default:
      return null;
  }
}

function runQuery(source: Row[], query: TableQuery): TableResult {
  // A field carries at most one active filter; a later filter on the same field
  // replaces the earlier one rather than compounding.
  const effective = new Map<string, FilterSpec>();
  for (const filter of query.filters) effective.set(filter.field, filter);

  const filtered = source.filter((row) =>
    [...effective.values()].every((filter) => matchesFilter(row, filter)),
  );

  const sortSpec = query.sort;
  const sorted = sortSpec ? [...filtered].sort((a, b) => compareRows(a, b, sortSpec)) : filtered;

  // Aggregations cover the whole filtered set, never the page.
  const aggregations: Record<string, number | null> = {};
  for (const spec of query.aggregations) aggregations[spec.field] = aggregate(filtered, spec);

  const total = sorted.length;
  const lastPage = Math.max(1, Math.ceil(total / query.pageSize));
  const page = Math.min(Math.max(query.page, 1), lastPage);
  const start = (page - 1) * query.pageSize;

  return {
    rows: sorted.slice(start, start + query.pageSize),
    total,
    page,
    pageSize: query.pageSize,
    aggregations,
  };
}

/** The data source the ledger consumes: the same shape a real endpoint would
 *  return, with the latency and the cancellation a network call has. */
function ledgerSource(rows: Row[]): DataSource {
  let requests = 0;
  return async (query, signal) => {
    requests += 1;
    await pause(160 + ((requests * 137) % 200), signal);
    return runQuery(rows, query);
  };
}

/* ---------------------------------------------------------------------------
   The ledger's shape
   ------------------------------------------------------------------------ */

const expenseRow = (expense: Expense, phaseName: (phaseId: string) => string): Row => ({
  id: expense.id,
  phase: phaseName(expense.phaseId),
  vendor: expense.vendorName,
  category: expense.category,
  status: expense.status,
  delivery: expense.delivery,
  date: expense.date,
  amount: expense.amount,
  forecast: expense.forecast,
  variance: expense.amount - expense.forecast,
  description: expense.description,
});

/** A `select` filter source over values that exist in this dataset, so the
 *  popover can only ever offer a value that can match. */
function optionsSource(options: RemoteOption[], placeholder: string): TableSelectSource {
  return {
    placeholder,
    minSearchLength: 1,
    search: async (term, signal) => {
      await pause(120, signal);
      const needle = term.trim().toLowerCase();
      const matched =
        needle === ''
          ? options
          : options.filter((option) => option.label.toLowerCase().includes(needle));
      return { options: matched, truncated: false };
    },
  };
}

/* The ledger's status and delivery cells carry the fixture's own codes, so the
   words a reader sees come from these maps — and the filter popover reads the
   same ones, which is why they hold KEYS rather than strings: only the point
   the cell and the options are built has a `t`.
   The ledger itself is the same ledger the portfolio screen renders, so the
   keys are that screen's: one entry per column header, status and placeholder,
   translated once and read by both. */
const EXPENSE_STATUS_LABELS: Record<Expense['status'], AppTranslationKey> = {
  approved: 'financials.expenseStatus.approved',
  'in-review': 'financials.expenseStatus.inReview',
  disputed: 'financials.expenseStatus.disputed',
};

const DELIVERY_LABELS: Record<Expense['delivery'], AppTranslationKey> = {
  delivered: 'financials.delivery.delivered',
  partial: 'financials.delivery.partial',
  pending: 'financials.delivery.pending',
  'not-applicable': 'financials.delivery.notApplicable',
};

/* The phase and participation codes resolve through the SHARED status maps: the
   schedule and the investors screens render these same records, so the words for
   them are one vocabulary rather than three copies free to drift apart. */
const PHASE_STATUS_LABELS: Record<SchedulePhase['status'], AppTranslationKey> = {
  completed: 'status.phase.completed',
  'in-progress': 'status.phase.inProgress',
  planned: 'status.phase.planned',
};

const PARTICIPATION_STATUS_LABELS: Record<Participation['status'], AppTranslationKey> = {
  confirmed: 'status.participation.confirmed',
  pending: 'status.participation.pending',
  draft: 'status.participation.draft',
};

/**
 * A cell formatter for an enum code.
 *
 * A code the map has not seen falls through as itself rather than as a blank:
 * an unrecognised token in a cell is a visible defect with an obvious cause,
 * and the popover's options are drawn from the same values, so a row the map
 * has missed is one the filter cannot offer either.
 */
function labelFormatter(
  t: AppTranslate,
  labels: Record<string, AppTranslationKey>,
): (value: RawCellValue) => string {
  return (value) => {
    if (typeof value !== 'string') return '';
    const key = labels[value];
    return key ? t(key) : value;
  };
}

function unique(values: string[], label: (value: string) => string = (value) => value): RemoteOption[] {
  return [...new Set(values)]
    .sort((a, b) => a.localeCompare(b, 'en'))
    .map((value) => ({ value, label: label(value) }));
}

/**
 * The options a filter may offer are read off this project's own ledger, so a
 * development cannot be filtered by a trade or a vendor it never used. Built in
 * the route, not at module scope, because the values differ per development —
 * and built from `t`, because the placeholder and the option labels are
 * interface text.
 */
function buildSelectSources(
  expenses: Expense[],
  t: AppTranslate,
  phaseName: (phaseId: string) => string,
): Record<string, TableSelectSource> {
  const phases = [...new Set(expenses.map((expense) => phaseName(expense.phaseId)))]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));

  return {
    phase: optionsSource(phases, t('financials.search.phases')),
    vendor: optionsSource(unique(expenses.map((expense) => expense.vendorName)), t('financials.search.vendors')),
    category: optionsSource(unique(expenses.map((expense) => expense.category)), t('financials.search.categories')),
    status: optionsSource(
      unique(expenses.map((expense) => expense.status), labelFormatter(t, EXPENSE_STATUS_LABELS)),
      t('financials.search.statuses'),
    ),
    delivery: optionsSource(
      unique(expenses.map((expense) => expense.delivery), labelFormatter(t, DELIVERY_LABELS)),
      t('financials.search.delivery'),
    ),
  };
}

const asMoney = (value: RawCellValue): string =>
  typeof value === 'number' ? formatMoney(value, { precision: 0 }) : '';
const asDay = (value: RawCellValue): string =>
  typeof value === 'string' ? formatDate(value, { style: 'medium' }) : '';
/* Built from `t` rather than held at module scope, because a header is
   interface text and a status cell is a code. The component memoises the result
   on `t`, which is what keeps the array identity the table memoises its own
   column model on. The headers are the portfolio ledger's keys: it is the same
   ledger, with the development column dropped because this screen has one. */
function ledgerColumns(t: AppTranslate, vocabulary: FixtureVocabulary): TableColumn[] {
  const asStatus = labelFormatter(t, EXPENSE_STATUS_LABELS);
  const asDelivery = labelFormatter(t, DELIVERY_LABELS);
  /* The category is a closed vocabulary in the fixture, so its cell holds the
     code and its word comes from the fixture's own catalogue. */
  const asCategory = (value: RawCellValue): string =>
    typeof value === 'string' ? vocabulary.expenseCategory(value) : '';

  return [
    { key: 'id', header: t('financials.column.ref'), width: 148, sortable: true, filterable: true, filterType: 'text' },
    { key: 'phase', header: t('financials.column.phase'), width: 248, sortable: true, filterable: true, filterType: 'select' },
    { key: 'vendor', header: t('financials.column.vendor'), width: 208, sortable: true, filterable: true, filterType: 'select' },
    { key: 'category', header: t('financials.column.category'), width: 172, sortable: true, filterable: true, filterType: 'select', formatter: asCategory },
    { key: 'status', header: t('common.status'), width: 140, sortable: true, filterable: true, filterType: 'select', formatter: asStatus },
    { key: 'delivery', header: t('financials.column.delivery'), width: 148, sortable: true, filterable: true, filterType: 'select', formatter: asDelivery },
    {
      key: 'date',
      header: t('common.date'),
      width: 148,
      sortable: true,
      filterable: true,
      filterType: 'dateRange',
      formatter: asDay,
    },
    {
      key: 'amount',
      header: t('common.amount'),
      width: 148,
      align: 'right',
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
      aggregate: { op: 'sum' },
    },
    {
      key: 'forecast',
      header: t('financials.column.forecast'),
      width: 148,
      align: 'right',
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
    },
    {
      key: 'variance',
      header: t('financials.column.variance'),
      width: 148,
      align: 'right',
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      // The sign is the value's own; formatting it never re-derives it.
      formatter: asMoney,
    },
    {
      key: 'description',
      header: t('financials.column.description'),
      width: 340,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
  ];
}

const TH =
  'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TD = 'px-3 py-2 text-body';
const TD_NUM = 'px-3 py-2 text-right font-mono text-body';
const FOOT = 'border-t-2 border-line-strong bg-surface-alt';

/* Each alert title below is one sentence whose noun and verb agree with a count,
   so each is stored as a plural pair and the language picks the form — glueing a
   number to a noun cannot be translated. */

const phaseTone = (status: SchedulePhase['status']): BadgeTone =>
  status === 'completed' ? 'success' : status === 'in-progress' ? 'info' : 'neutral';

const participationTone = (status: Participation['status']): BadgeTone =>
  status === 'confirmed' ? 'success' : status === 'pending' ? 'warning' : 'neutral';

/**
 * The money tab of one development: where the phased budget went, which lines
 * carry it, and who has funded it.
 *
 * The headline figures are not repeated here — `ProjectShell` owns them. What
 * this page adds is the reconciliation beneath them: the phase actuals that make
 * up "spent to date", and the ledger whose subtotal has to agree with it.
 */
export function ProjectFinancialsPage() {
  const { projectId } = useParams();
  const id = projectId ?? '';
  const t = useT();

  /*
     The ledger and its phase table, resolved for the active locale, and resolved
     BEFORE the not-found return below because a hook cannot run after a
     conditional one — the empty development selects no rows, and that branch
     renders an error state rather than the table.
  */
  const expenses = useExpenses(id);
  const phases = useScheduleOf(id);
  const vocabulary = useFixtureVocabulary();

  // Memoised so the table's effect does not see a new source on every render.
  // `expenses` and `vocabulary` are in the list because both are stable for the
  // life of a locale — a new one means the words in the rows have changed.
  const source = useMemo(
    () => ledgerSource(expenses.map((expense) => expenseRow(expense, vocabulary.phase))),
    [expenses, vocabulary],
  );
  const selectSources = useMemo(
    () => buildSelectSources(expenses, t, vocabulary.phase),
    [expenses, t, vocabulary],
  );
  const columns = useMemo(() => ledgerColumns(t, vocabulary), [t, vocabulary]);

  const project = findProject(projectId);

  if (!project) {
    return (
      <Panel padding="lg" title={t('project.shell.notFoundTitle')}>
        <ErrorState
          variant="page"
          title={t('project.shell.notFoundHeading')}
          message={t('project.financials.notFoundMessage', { id: projectId ?? '' })}
        />
      </Panel>
    );
  }

  const totals = projectTotals(project.id);
  const participants = participationsOf(project.id);

  const budgetedTotal = phases.reduce((sum, phase) => sum + phase.budgeted, 0);
  const actualTotal = phases.reduce((sum, phase) => sum + phase.actual, 0);
  const varianceTotal = actualTotal - budgetedTotal;

  const committedTotal = participants.reduce((sum, record) => sum + record.committed, 0);
  const contributedTotal = participants.reduce((sum, record) => sum + record.contributed, 0);
  const callsIssuedTotal = participants.reduce((sum, record) => sum + record.callsIssued, 0);
  const callsSettledTotal = participants.reduce((sum, record) => sum + record.callsSettled, 0);

  const unverified = expenses.filter((expense) => expense.status === 'in-review');
  const disputed = expenses.filter((expense) => expense.status === 'disputed');
  const aheadOfDelivery = expenses.filter(
    (expense) => expense.delivery === 'pending' || expense.delivery === 'partial',
  );
  const valueOf = (rows: Expense[]) => rows.reduce((sum, expense) => sum + expense.amount, 0);

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title={t('project.financials.phases.title')}
        description={t('project.financials.phases.description', {
          amount: formatMoney(totals.actual),
        })}
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="dhd-sr-only">
              {t('project.financials.phases.caption', { name: project.name })}
            </caption>
            <thead>
              <tr className="border-b-2 border-line-strong">
                <th scope="col" className={cn(TH, 'text-left')}>
                  {t('financials.column.phase')}
                </th>
                <th scope="col" className={cn(TH, 'text-left')}>
                  {t('common.status')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.budgeted')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.actual')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('financials.column.variance')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.used')}
                </th>
              </tr>
            </thead>
            <tbody>
              {phases.map((phase) => (
                <tr key={phase.id} className="border-b border-line-subtle hover:bg-surface-alt">
                  <th scope="row" className={cn(TD, 'text-left font-normal')}>
                    {phase.name}
                  </th>
                  <td className={TD}>
                    <Badge tone={phaseTone(phase.status)} variant="subtle" size="sm">
                      {t(PHASE_STATUS_LABELS[phase.status])}
                    </Badge>
                  </td>
                  <td className={cn(TD_NUM, 'font-mono')}>{formatMoney(phase.budgeted)}</td>
                  <td className={cn(TD_NUM, 'font-mono')}>{formatMoney(phase.actual)}</td>
                  <td
                    className={cn(
                      TD_NUM,
                      'font-mono',
                      phase.actual - phase.budgeted > 0 ? 'text-danger' : 'text-success',
                    )}
                  >
                    {formatMoney(phase.actual - phase.budgeted)}
                  </td>
                  <td className={cn(TD_NUM, 'font-mono')}>
                    {phase.budgeted === 0
                      ? '—'
                      : formatPercentage((phase.actual / phase.budgeted) * 100, { precision: 0 })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={FOOT}>
                <th
                  scope="row"
                  colSpan={2}
                  className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-heading"
                >
                  <span className="dhd-sr-only">{t('common.total')} </span>
                  {t('project.financials.phaseCount', { count: phases.length })}
                </th>
                <td className={cn(TD_NUM, 'font-mono text-heading')}>{formatMoney(budgetedTotal)}</td>
                <td className={cn(TD_NUM, 'font-mono text-heading')}>{formatMoney(actualTotal)}</td>
                <td
                  className={cn(
                    TD_NUM,
                    'font-mono',
                    varianceTotal > 0 ? 'text-danger' : 'text-success',
                  )}
                >
                  {formatMoney(varianceTotal)}
                </td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>

      <Progress
        value={totals.actual}
        max={totals.budgeted}
        tone="primary"
        showValue
        label={t('project.financials.budgetConsumed', { name: project.name })}
      />

      {unverified.length > 0 ? (
        <InlineAlert
          tone="warning"
          title={t('project.financials.unverified.title', { count: unverified.length })}
          description={t('project.financials.unverified.description', {
            amount: formatMoney(valueOf(unverified)),
          })}
        />
      ) : null}

      {aheadOfDelivery.length > 0 ? (
        <InlineAlert
          tone="info"
          title={t('project.financials.aheadOfDelivery.title', { count: aheadOfDelivery.length })}
          description={t('project.financials.aheadOfDelivery.description', {
            amount: formatMoney(valueOf(aheadOfDelivery)),
          })}
        />
      ) : null}

      {/*
        No disputed line exists in the fixtures today. The branch is written
        anyway, so a later one cannot be posted quietly: the day one appears, the
        ledger says so above the amount it would otherwise be read as approved.
      */}
      {disputed.length > 0 ? (
        <InlineAlert
          tone="error"
          title={t('project.financials.disputed.title', { count: disputed.length })}
          description={t('project.financials.disputed.description', {
            amount: formatMoney(valueOf(disputed)),
          })}
        />
      ) : null}

      <Panel
        title={t('financials.ledger.title')}
        description={t('project.financials.ledger.description')}
      >
        <PaginatedTable
          key={projectId}
          id={`project-expenses-${projectId}`}
          caption={t('project.financials.ledger.caption', { name: project.name })}
          columns={columns}
          dataSource={source}
          rowKey="id"
          selectSources={selectSources}
          pageSizeOptions={[10, 25, 50]}
          defaultPageSize={10}
          emptyTitle={t('project.financials.ledger.emptyTitle')}
          errorTitle={t('financials.ledger.errorTitle')}
        />
      </Panel>

      <Panel
        title={t('project.financials.funding.title')}
        description={t('project.financials.funding.description', {
          amount: formatMoney(totals.contributed),
        })}
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="dhd-sr-only">
              {t('project.financials.funding.caption', { name: project.name })}
            </caption>
            <thead>
              <tr className="border-b-2 border-line-strong">
                <th scope="col" className={cn(TH, 'text-left')}>
                  {t('project.financials.column.investor')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.share')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.committed')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.contributed')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.callsIssued')}
                </th>
                <th scope="col" className={cn(TH, 'text-right')}>
                  {t('project.financials.column.callsSettled')}
                </th>
                <th scope="col" className={cn(TH, 'text-left')}>
                  {t('common.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {participants.map((record) => {
                const investor = findInvestor(record.investorId);
                return (
                  <tr key={record.id} className="border-b border-line-subtle hover:bg-surface-alt">
                    <th scope="row" className={cn(TD, 'text-left font-normal')}>
                      {investor?.name ?? record.investorId}
                    </th>
                    <td className={cn(TD_NUM, 'font-mono')}>
                      {formatPercentage(record.sharePercent, { precision: 0 })}
                    </td>
                    <td className={cn(TD_NUM, 'font-mono')}>{formatMoney(record.committed)}</td>
                    <td className={cn(TD_NUM, 'font-mono')}>{formatMoney(record.contributed)}</td>
                    <td className={cn(TD_NUM, 'font-mono')}>{formatNumber(record.callsIssued)}</td>
                    <td className={cn(TD_NUM, 'font-mono')}>{formatNumber(record.callsSettled)}</td>
                    <td className={TD}>
                      <Badge tone={participationTone(record.status)} variant="subtle" size="sm">
                        {t(PARTICIPATION_STATUS_LABELS[record.status])}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={FOOT}>
                <th
                  scope="row"
                  className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-heading"
                >
                  <span className="dhd-sr-only">{t('common.total')} </span>
                  {t('project.financials.investorCount', { count: participants.length })}
                </th>
                <td className="px-3 py-2" />
                <td className={cn(TD_NUM, 'font-mono text-heading')}>{formatMoney(committedTotal)}</td>
                <td className={cn(TD_NUM, 'font-mono text-heading')}>
                  {formatMoney(contributedTotal)}
                </td>
                <td className={cn(TD_NUM, 'font-mono text-heading')}>
                  {formatNumber(callsIssuedTotal)}
                </td>
                <td className={cn(TD_NUM, 'font-mono text-heading')}>
                  {formatNumber(callsSettledTotal)}
                </td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>
    </div>
  );
}
